var m = require('mithril');
var prop = require('mithril/stream');
var moment = require('moment');
var masonry = require('../libs/masonry');
var auth = require('../auth/models/session');
var loaders = require('../layout/components/loaders');
var heading = require('../components/heading');
var modal = require('../layout/components/modal');
var header = require('../layout/components/header');
var edit = require('../layout/components/edit');
var pathHeader = require('../layout/components/pathHeader');
var element = require('../dashboards/components/element');
var addLocationModal = require('./addLocationModal');
var addBuildingModal = require('./addBuildingModal')
var addLevelModal = require('./addLevelModal');
var addRoomModal = require('./addRoomModal');
var addCustomEquipmentModal = require('../equipment/addCustomEquipmentModal');
var addDashboardModal = require('../dashboards/components/addDashboardModal');
var addDiagramModal = require('../diagrams/addDiagramModal');
var LevelSVGModal = require('./updateLevelSVGModal');
var moveDashboardModal = require('../dashboards/models/moveDashboardModal');
var copyModal = require('../dashboards/models/copyModal');
var Draggable = require('../libs/draggable');
var eventTable = require('../alarms/components/alarmEventTable');
var mapElement = require('../maps/mapElement');
var equipmentModel = require('../equipment/models/equipment');
var weatherChart = require('./components/weatherChart');
var alarmsTable = require('../alarms/alarmsTable');
var { getChildLocations, getGrandchildLocations, setRoomClassTree, isLocationClass, isRoomClass } = require('../maps/mapUtils');
var RequestStatus = require('../layout/enums/RequestStatus');
var requestStatusHandler = require('../layout/components/requestStatusHandler');
var downloadReportModalInterface = require('../layout/components/downloadReportModalInterface');
var searchEquipmentComponent = require('./components/searchEquipmentComponent');
var overridesComponent = require('./components/overridesComponent');
var eventsComponent = require('./components/eventsComponent');
var pointSubscriberClass = require('../components/pointSubscriberClass');
var hoverableDropdownDiv = require('../components/hoverableDropdownDiv');
var smartChart = require('../dashboards/components/smartChart');
var addDatasetOrElementModal = require('./components/addDatasetOrElementModal');
var bulkCreateEquipmentModal = require('./components/bulkCreateEquipmentModal');
var locationDescendantSearch = require('./components/locationDescendantSearch');
var viewEventTracker = require('../events/viewEventTracker');
var rowActionDropdown = require('../components/rowActionDropdown');
var pageTitle = require('../layout/pageTitle');
var flash = require('../components/flash');

var roomClassTreeCache = null;
var roomClassTreePromise = null;

function loadRoomClassTree() {
  if (roomClassTreeCache) return Promise.resolve(roomClassTreeCache);
  if (roomClassTreePromise) return roomClassTreePromise;
  roomClassTreePromise = loaders.requestWithLoader({
    url: '/api/v1/ontology/classtree?type=room',
    method: 'GET',
    background: true
  })
  .then(tree => {
    roomClassTreeCache = tree || null;
    if (roomClassTreeCache) {
      setRoomClassTree(roomClassTreeCache);
    }
    return roomClassTreeCache;
  })
  .catch(error => {
    console.error('Error loading room class tree:', error);
    return null;
  })
  .finally(() => {
    roomClassTreePromise = null;
  });
  return roomClassTreePromise;
}

function buildLocationDetailUrl(rid, options) {
  var base = rid ? `/api/v1/location/${rid}/detail` : '/api/v1/location/detail';
  var params = Object.keys(options || {}).map(function (key) {
    return `${encodeURIComponent(key)}=${encodeURIComponent(options[key])}`;
  }).join('&');
  return params ? `${base}?${params}` : base;
}

function normalizeRidLike(value) {
  if (!value) return null;
  var stringValue = String(value).trim();
  if (!stringValue) return null;
  return stringValue.charAt(0) === '#' ? stringValue.slice(1) : stringValue;
}

function buildSavedReportLookupKey(reportType, locationRid) {
  var normalizedReportType = typeof reportType === 'string' ? reportType.trim() : '';
  var normalizedLocationRid = normalizeRidLike(locationRid);
  if (!normalizedReportType || !normalizedLocationRid) return null;
  return normalizedLocationRid + '::' + normalizedReportType;
}

function normalizeDashboardEdge(edge, useHasDashboardEdge, fallbackEdgeClass) {
  if (!edge || !edge.in || edge.in['@class'] !== 'Dashboard') return null;

  return Object.assign({}, edge, {
    '@class': edge['@class'] || fallbackEdgeClass,
    useHasDashboardEdge: useHasDashboardEdge
  });
}

function getDashboardEdgesForLocation(location) {
  if (!location) return [];

  var seenDashboardRids = {};
  var dashboardEdges = [];

  function appendEdges(edges, useHasDashboardEdge, fallbackEdgeClass) {
    if (!Array.isArray(edges)) return;
    edges.forEach(function (edge) {
      var normalizedEdge = normalizeDashboardEdge(edge, useHasDashboardEdge, fallbackEdgeClass);
      var dashboardRid = normalizedEdge && normalizedEdge.in && normalizedEdge.in['@rid'];
      if (!normalizedEdge || !dashboardRid || seenDashboardRids[dashboardRid]) return;

      seenDashboardRids[dashboardRid] = true;
      dashboardEdges.push(normalizedEdge);
    });
  }

  if (location['@class'] === 'Dashboard') {
    appendEdges(location.out_Contains, false, 'Contains');
  }

  appendEdges(location.out_hasDashboard, true, 'hasDashboard');
  return dashboardEdges;
}

function getDashboardRidsForLocation(location) {
  return getDashboardEdgesForLocation(location)
    .map(function (edge) {
      return edge && edge.in && edge.in['@rid'];
    })
    .filter(Boolean);
}

function persistSortOrder(ctrl, request, failureMessage) {
  return loaders.requestWithLoader(request)
    .then(function (result) {
      return Promise.resolve(ctrl.init()).then(function () {
        return result;
      });
    })
    .catch(function (error) {
      console.error('Unable to save reordered location data:', error);
      if (typeof ctrl.showSortError === 'function') {
        ctrl.showSortError(failureMessage || 'Unable to save the new order. The latest saved order was reloaded.');
      }
      m.redraw();
      return Promise.resolve(ctrl.init())
        .catch(function (reloadError) {
          console.error('Unable to reload location after an order failure:', reloadError);
          return null;
        })
        .then(function () {
          return null;
        });
    });
}

var oninit = function () {
  var ctrl = this;
  ctrl.rid = m.route.param('id');
  header.vm.activeTab('Locations');

  document.title = 'nimrod | Locations';

  ctrl.mapSelectedView = prop('Overview');
  ctrl.previousMapSelectedView = prop('Overview');
  ctrl.mapModes = prop([]);

  ctrl.syncMapSelectedView = function () {
    var mapModes = Array.isArray(ctrl.mapModes())
      ? ctrl.mapModes()
      : [];
    if (!mapModes.length) return;
    var names = mapModes
      .map(function (mapMode) { return mapMode && mapMode.name ? mapMode.name : null; })
      .filter(function (name) { return !!name; });
    if (!names.length) return;
    var current = ctrl.mapSelectedView();
    if (names.indexOf(current) === -1) {
      var fallback = names.indexOf('Overview') > -1 ? 'Overview' : names[0];
      ctrl.mapSelectedView(fallback);
    }
  };

  ctrl.loadMapModes = function () {
    return loaders.requestWithLoader({
      loader: 'background',
      method: 'GET',
      url: '/api/v1/mapmodes',
    })
      .then(function (response) {
        if (Array.isArray(response)) {
          ctrl.mapModes(response);
        }
        ctrl.syncMapSelectedView();
      })
      .catch(function () {
        ctrl.syncMapSelectedView();
      });
  };

  ctrl.loadMapModes();



  ctrl.location = prop();
  ctrl.equipmentLocations = prop([]);
  ctrl.parentLocation = prop();
  ctrl.grandparentLocation = prop();
  ctrl.siblingLocations = prop([]);
  ctrl.equipment = prop([]);
  ctrl.searchEquipmentValue = prop('');
  ctrl.searchEquipmentActive = prop(false);
  ctrl.searchedEquipmentRids = prop([]);
  ctrl.alarmEquipmentRids = prop([]);
  ctrl.alarmEquipmentLoading = prop(false);
  ctrl.alarmEquipmentRequestId = 0;
  ctrl.searchStartsRequestsForEveryTable = prop({});
  ctrl.preferredNumberOfRows = prop(undefined);
  ctrl.preferredNumberOfRowsValue = prop(10);
  ctrl.lastLocation = prop();
  ctrl.requestStatus = prop(RequestStatus.PENDING);
  ctrl.requestStatusCode = prop();
  ctrl.sortError = prop([]);
  ctrl.sortErrorTimer = null;
  ctrl.equipmentClassesSelected = prop([]);
  ctrl.equipmentTableLoading = prop(false);
  ctrl.childLocations = prop([]);
  ctrl.grandchildLocations = prop([]);
  ctrl.openDropdownId = null;
  ctrl.overrideRids = prop([]);
  ctrl.overridesRecords = prop([]);
  ctrl.overridesLoading = prop(false);
  ctrl.overrideLoadRequestId = 0;
  ctrl.overrideEntries = prop([]);
  ctrl.overrideSortAscending = prop(false);
  ctrl.overrideSortToken = prop('desc');
  ctrl.overrideRefreshToken = prop(0);
  ctrl.locationDropdownOpen = prop(false);
  ctrl.locationDropdownPosition = prop({ top: null, left: null });
  ctrl.locationDropdownArrowElement = null;
  ctrl.locationDropdownElement = null;
  ctrl.locationDropdownDimensions = prop({ width: 0, height: 0 });
  ctrl.locationDropdownVisible = prop(false);
  ctrl.isMapFullscreen = prop(false);
  ctrl.savedReportRecordsByKey = {};
  ctrl.savedReportRecordPromisesByKey = {};
  ctrl.closeLocationDropdown = function () {
    ctrl.locationDropdownOpen(false);
    ctrl.locationDropdownVisible(false);
  };

  ctrl.showSortError = function (message) {
    if (ctrl.sortErrorTimer) {
      clearTimeout(ctrl.sortErrorTimer);
      ctrl.sortErrorTimer = null;
    }
    ctrl.sortError(message ? [message] : []);
    ctrl.sortErrorTimer = setTimeout(function () {
      ctrl.sortError([]);
      ctrl.sortErrorTimer = null;
      m.redraw();
    }, 5000);
  };

  ctrl.findSavedLocationReportRecord = function (reportType, locationRid) {
    var lookupKey = buildSavedReportLookupKey(reportType, locationRid);
    if (!lookupKey) return Promise.resolve(null);

    if (Object.prototype.hasOwnProperty.call(ctrl.savedReportRecordsByKey, lookupKey)) {
      return Promise.resolve(ctrl.savedReportRecordsByKey[lookupKey]);
    }

    if (ctrl.savedReportRecordPromisesByKey[lookupKey]) {
      return ctrl.savedReportRecordPromisesByKey[lookupKey];
    }

    ctrl.savedReportRecordPromisesByKey[lookupKey] = loaders.requestWithLoader({
      loader: 'background',
      url: '/api/v1/report/',
      method: 'GET',
      params: {
        locationRid: normalizeRidLike(locationRid),
        type: reportType
      },
      background: true
    })
    .then(function (records) {
      var matchingRecords = (Array.isArray(records) ? records : []).filter(function (record) {
        return normalizeRidLike(record && record.locationRid) === normalizeRidLike(locationRid)
          && String(record && record.type || '') === String(reportType || '');
      });
      var selectedRecord = matchingRecords.length === 1 ? matchingRecords[0] : null;
      if (matchingRecords.length > 1) {
        console.warn('Multiple saved report records match this location report menu item; falling back to direct report flow.', {
          reportType: reportType,
          locationRid: locationRid,
          reportRids: matchingRecords.map(function (record) { return record && record['@rid']; })
        });
      }
      ctrl.savedReportRecordsByKey[lookupKey] = selectedRecord;
      return selectedRecord;
    })
    .catch(function (error) {
      console.error('Error loading saved report record for location report menu:', error);
      ctrl.savedReportRecordsByKey[lookupKey] = null;
      return null;
    })
    .finally(function () {
      delete ctrl.savedReportRecordPromisesByKey[lookupKey];
    });

    return ctrl.savedReportRecordPromisesByKey[lookupKey];
  };

  ctrl.setLocationDropdownElement = function (element) {
    if (!element) {
      ctrl.locationDropdownElement = null;
      ctrl.locationDropdownDimensions({ width: 0, height: 0 });
      ctrl.locationDropdownVisible(false);
      return;
    }
    ctrl.locationDropdownElement = element;
    if (!ctrl.locationDropdownOpen()) return;
    var needsRedraw = false;
    if (ctrl.measureLocationDropdown(element)) needsRedraw = true;
    if (ctrl.updateLocationDropdownPosition()) needsRedraw = true;
    if (!ctrl.locationDropdownVisible()) {
      ctrl.locationDropdownVisible(true);
      needsRedraw = true;
    }
    if (needsRedraw) m.redraw();
  };

  ctrl.measureLocationDropdown = function (element) {
    if (typeof window === 'undefined') return false;
    if (!ctrl.locationDropdownOpen()) return false;
    var dropdownElement = element || ctrl.locationDropdownElement;
    if (!dropdownElement || typeof dropdownElement.getBoundingClientRect !== 'function') return false;
    var rect = dropdownElement.getBoundingClientRect();
    var currentDimensions = ctrl.locationDropdownDimensions() || {};
    if (currentDimensions.width === rect.width && currentDimensions.height === rect.height) return false;
    ctrl.locationDropdownDimensions({
      width: rect.width,
      height: rect.height
    });
    return true;
  };

  ctrl.setLocationDropdownArrow = function (element) {
    if (ctrl.locationDropdownArrowElement !== element) {
      ctrl.locationDropdownArrowElement = element || null;
    }
    if (element && ctrl.locationDropdownOpen()) {
      ctrl.updateLocationDropdownPosition();
    }
  };

  ctrl.updateLocationDropdownPosition = function () {
    if (typeof window === 'undefined') return false;
    if (!ctrl.locationDropdownArrowElement) return false;
    var rect = ctrl.locationDropdownArrowElement.getBoundingClientRect();
    var viewportWidth = window.innerWidth || (document.documentElement && document.documentElement.clientWidth) || 0;
    var viewportHeight = window.innerHeight || (document.documentElement && document.documentElement.clientHeight) || 0;
    var dropdownDimensions = ctrl.locationDropdownDimensions() || {};
    var dropdownWidth = dropdownDimensions.width || 0;
    var dropdownHeight = dropdownDimensions.height || 0;
    var viewportMargin = 8;
    var left = rect.left;
    if (dropdownWidth && viewportWidth) {
      var maxLeft = Math.max(viewportMargin, viewportWidth - dropdownWidth - viewportMargin);
      left = Math.min(Math.max(viewportMargin, left), maxLeft);
    } else {
      left = Math.max(viewportMargin, left);
    }
    var top = rect.bottom;
    if (dropdownHeight && viewportHeight) {
      var maxTop = Math.max(viewportMargin, viewportHeight - dropdownHeight - viewportMargin);
      top = Math.min(Math.max(viewportMargin, top), maxTop);
    } else {
      top = Math.max(viewportMargin, top);
    }
    var previousPosition = ctrl.locationDropdownPosition() || {};
    if (previousPosition.top === top && previousPosition.left === left) return false;
    ctrl.locationDropdownPosition({
      top: top,
      left: left
    });
    return true;
  };

  ctrl.handleLocationDropdownViewportChange = function () {
    if (!ctrl.locationDropdownOpen()) return;
    var needsRedraw = false;
    if (ctrl.measureLocationDropdown()) needsRedraw = true;
    if (ctrl.updateLocationDropdownPosition()) needsRedraw = true;
    if (needsRedraw) m.redraw();
  };

  ctrl.handleLocationDropdownScroll = function (event) {
    if (!ctrl.locationDropdownOpen()) return;
    if (ctrl.locationDropdownElement && event && event.target) {
      var target = event.target;
      var scrolledWithinDropdown = target === ctrl.locationDropdownElement || ctrl.locationDropdownElement.contains(target);
      if (scrolledWithinDropdown) return;
    }
    ctrl.closeLocationDropdown();
    m.redraw();
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('resize', ctrl.handleLocationDropdownViewportChange);
    window.addEventListener('scroll', ctrl.handleLocationDropdownScroll, true);
  }

  function toTimestamp(value) {
    if (value === null || typeof value === 'undefined') return -Infinity;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      var numeric = Number(value);
      if (!Number.isNaN(numeric)) return numeric;
      var parsedDate = Date.parse(value);
      return Number.isNaN(parsedDate) ? -Infinity : parsedDate;
    }
    var parsed = Date.parse(value);
    return Number.isNaN(parsed) ? -Infinity : parsed;
  }

  ctrl.sortOverrideEntries = function () {
    var entries = Array.isArray(ctrl.overrideEntries()) ? ctrl.overrideEntries().slice() : [];
    entries.sort(function (a, b) {
      var tsA = toTimestamp(a && a.timestamp);
      var tsB = toTimestamp(b && b.timestamp);
      return ctrl.overrideSortAscending() ? tsA - tsB : tsB - tsA;
    });
    ctrl.overrideEntries(entries);
    ctrl.overrideRids(entries.map(function (entry) { return entry.rid; }));
  };

  ctrl.toggleOverrideSort = function () {
    var nextAscending = !ctrl.overrideSortAscending();
    ctrl.overrideSortAscending(nextAscending);
    ctrl.overrideSortToken(nextAscending ? 'asc' : 'desc');
    ctrl.sortOverrideEntries();
    ctrl.overrideRefreshToken(ctrl.overrideRefreshToken() + 1);
  };
  // Reports (for Room)
  let today = new Date();
  let monthAgo = new Date();
  monthAgo.setMonth(today.getMonth() - 1);
  ctrl.reportStartDateAndEndDate = prop([moment(monthAgo), moment(today)]);

  ctrl.computeFeatureGroups = function () {
    ctrl.childLocations(
      getChildLocations(ctrl.location(), ctrl.equipmentLocations())
    );
    ctrl.grandchildLocations(
      getGrandchildLocations(ctrl.location(), ctrl.equipmentLocations())
    );
  };

  ctrl.loadOverrides = async function () {
    const requestId = ++ctrl.overrideLoadRequestId;
    if (!ctrl.location() || !ctrl.location()['@rid']) {
      ctrl.overrideRids([]);
      ctrl.overridesRecords([]);
      ctrl.overridesLoading(false);
      return;
    }

    const locationRidRaw = ctrl.location()['@rid'];
    const locationRidParam = (typeof locationRidRaw === 'string' && locationRidRaw.charAt(0) === '#')
      ? locationRidRaw.slice(1)
      : locationRidRaw;

    ctrl.overridesLoading(true);
    try {
      const response = await loaders.requestWithLoader({
        url: '/api/v1/overrides/rids',
        method: 'GET',
        params: {
          locationRid: locationRidParam
        },
        background: true
      });
      if (requestId !== ctrl.overrideLoadRequestId) return;

      const entries = [];
      const seenRids = new Set();
      if (Array.isArray(response)) {
        response.forEach(function (item) {
          if (!item) return;
          var rid;
          var timestamp = null;
          if (typeof item === 'string') {
            rid = item;
          } else {
            rid = item['@rid'] || item.rid;
            timestamp = typeof item.timestamp !== 'undefined' ? item.timestamp : null;
          }
          if (!rid || seenRids.has(rid)) return;
          seenRids.add(rid);
          entries.push({ rid: rid, timestamp: timestamp });
        });
      }

      ctrl.overrideEntries(entries);
      ctrl.overrideSortAscending(false);
      ctrl.overrideSortToken('desc');
      ctrl.sortOverrideEntries();
      ctrl.overrideRefreshToken(ctrl.overrideRefreshToken() + 1);
    } catch (error) {
      if (requestId !== ctrl.overrideLoadRequestId) return;
      console.error('Error loading overrides:', error);
      ctrl.overrideRids([]);
      ctrl.overridesRecords([]);
      ctrl.overrideEntries([]);
      ctrl.overrideSortAscending(false);
      ctrl.overrideSortToken('desc');
    } finally {
      if (requestId === ctrl.overrideLoadRequestId) {
        ctrl.overridesLoading(false);
        m.redraw();
      }
    }
  };

  ctrl.init = async function () {
    viewEventTracker.recordCurrentViewEvent();
    ctrl.requestStatus(RequestStatus.PENDING);
    ctrl.closeLocationDropdown();
    try {
      ctrl.lastLocation(ctrl.location());
      var url = buildLocationDetailUrl(ctrl.rid, {
        includePath: 1,
        includeChildren: 1,
        includeContains: 1,
        includeDashboards: 1,
        includeDiagrams: 1,
        includeElements: 1,
        includeConnections: 1,
        includeFeedsPaths: 0,
        includeSystems: 0,
        includePermissions: 0,
        includeWeather: 1
      });
      var data = await loaders.requestWithLoader({
        loader: 'main',
        url: url,
        method: 'GET',
        extract: function (xhr) {
          const response = {
            status: xhr.status,
            body: xhr.responseText ? JSON.parse(xhr.responseText) : null
          };
          if (xhr.status < 200 || xhr.status >= 300) {
            throw { response: response };
          }
          return response.body;
        }
      });

      ctrl.location(data);
      ctrl.buildings = ctrl.location().out_isSiteOf || [];
      ctrl.buildings.sort((a, b) => a.in.index - b.in.index);
      ctrl.dashboards = [];
      ctrl.diagrams = [];
      ctrl.location().path.reverse();
      pageTitle.set(ctrl.location(), { fallback: 'Locations' });
      ctrl.parentLocation(ctrl.location().path[ctrl.location().path.length - 2]);
      ctrl.grandparentLocation(ctrl.location().path[ctrl.location().path.length - 3]);
      ctrl.computeFeatureGroups();
      ctrl.loadAlarmEquipmentRids();

      if (ctrl.location().defaultMapView) {
        ctrl.mapSelectedView(ctrl.location().defaultMapView);
        ctrl.syncMapSelectedView();
      }

      ctrl.dashboards = getDashboardEdgesForLocation(ctrl.location());
      ctrl.diagrams = ctrl.location().out_hasDiagram || [];

      ctrl.preferredNumberOfRows(data.preferredNumberOfRows ? JSON.parse(data.preferredNumberOfRows) : {});
      ctrl.preferredNumberOfRowsValue(parseInt(ctrl.preferredNumberOfRows()[auth.user()['@rid']] || 10));


      function buildLocationClassOptions(roomClassTree) {
        var options = [];

        function traverse(node, path) {
          if (!node || !node.name) return;
          const currentPath = path ? `${path} > ${node.name}` : node.name;
          options.push({
            value: node.name,
            key: currentPath,
          });

          // Recursively process children if they exist
          if (node.children && Array.isArray(node.children)) {
            node.children.forEach((child) => traverse(child, currentPath));
          }
        }

        (equipmentModel.equipmentClassTree.categories || []).forEach((category) => traverse(category, ''));
        if (roomClassTree && Array.isArray(roomClassTree.categories)) {
          roomClassTree.categories.forEach((category) => traverse(category, ''));
        }
        ensureClassOption(options, 'Building');
        ensureClassOption(options, 'Level');
        ensureClassOption(options, 'Room');
        return options;
      }

      ctrl.equipmentClassesArray = buildLocationClassOptions(roomClassTreeCache);
      loadRoomClassTree().then(function (roomClassTree) {
        ctrl.equipmentClassesArray = buildLocationClassOptions(roomClassTree);
        m.redraw();
      });

      await ctrl.getEquipmentLocations();
      ctrl.computeFeatureGroups();

      try {
        await ctrl.getSiblingLocations();
      }
      catch (error) {
        console.error('Error getting sibling locations:', error);
        ctrl.siblingLocations([]);
      }
      ctrl.loadOverrides();
      ctrl.requestStatus(RequestStatus.SUCCESS);
      m.redraw();
    } catch (error) {
      console.error('Error initializing control:', error);
      ctrl.requestStatusCode(error?.status);
      ctrl.requestStatus(RequestStatus.ERROR);
    }
  };

  ctrl.loadAlarmEquipmentRids = async function () {
    var location = ctrl.location && ctrl.location();
    if (!location || !location['@rid']) {
      ctrl.alarmEquipmentRids([]);
      ctrl.alarmEquipmentLoading(false);
      return;
    }

    var requestId = ++ctrl.alarmEquipmentRequestId;
    ctrl.alarmEquipmentLoading(true);
    ctrl.alarmEquipmentRids([]);
    try {
      var data = await loaders.requestWithLoader({
        loader: 'background',
        url: '/api/v1/entities/search',
        method: 'GET',
        params: {
          families: 'Equipment,Zone',
          mode: 'rids',
          locationRid: location['@rid'].slice(1)
        },
        background: true
      });
      if (ctrl.alarmEquipmentRequestId !== requestId) {
        return;
      }
      ctrl.alarmEquipmentRids(data || []);
    } catch (error) {
      console.error('Error loading alarm equipment list', error);
      if (ctrl.alarmEquipmentRequestId === requestId) {
        ctrl.alarmEquipmentRids([]);
      }
    } finally {
      if (ctrl.alarmEquipmentRequestId === requestId) {
        ctrl.alarmEquipmentLoading(false);
        m.redraw();
      }
    }
  };


  ctrl.getEquipmentLocations = async function () {
    if (ctrl.mapSelectedView() == 'Overview') {
      return ctrl.equipmentLocations([]);
    }

    let response = await loaders.requestWithLoader({
      url: `/api/v1/site/${ctrl.location()['@rid'].slice(1)}/equipmentcoordinates/${ctrl.mapSelectedView() == 'Overview' ? 'Equipment' : ctrl.mapSelectedView()}`,
      method: 'GET',
      background: true
    });

    let normalizedRecords = response.map(r => { return { in: r } });
    ctrl.equipmentLocations(normalizedRecords);

  };

  ctrl.getSiblingLocations = async function getSiblingLocations() {
    if (!ctrl.location() || !Array.isArray(ctrl.location().path) || ctrl.location().path.length < 2) {
      ctrl.siblingLocations([]);
      return;
    }

    var parentRecord = ctrl.location().path[ctrl.location().path.length - 2];
    if (!parentRecord || !parentRecord['@rid']) {
      ctrl.siblingLocations([]);
      return;
    }

    var parentData = await loaders.requestWithLoader({
      loader: 'main',
      method: 'GET',
      url: buildLocationDetailUrl(parentRecord['@rid'].slice(1), {
        includePath: 0,
        includeChildren: 1,
        includeContains: 1,
        includeDashboards: 0,
        includeDiagrams: 0,
        includeElements: 0,
        includeConnections: 0,
        includeFeedsPaths: 0,
        includeSystems: 0,
        includePermissions: 0,
        includeWeather: 0
      })
    });

    var childEdges = getChildEdgesFromParent(parentData);
    if (!Array.isArray(childEdges)) {
      ctrl.siblingLocations([]);
      return;
    }

    var currentRid = ctrl.location()['@rid'] ? ctrl.location()['@rid'].toString() : null;
    var siblingEdges = childEdges.filter(function (edge) {
      var node = edge && (edge.in || edge);
      if (!node || !node['@rid']) return false;
      if (currentRid && node['@rid'].toString() === currentRid) return false;
      return isLocationClass(node['@class']);
    });

    ctrl.siblingLocations(siblingEdges);
  }

  function getChildEdgesFromParent(parentData) {
    if (!parentData || typeof parentData !== 'object') return [];
    var parentClass = parentData['@class'];

    if (parentClass === 'Site' && Array.isArray(parentData.out_isSiteOf)) {
      return parentData.out_isSiteOf.slice();
    }

    if (
      (parentClass === 'Building' || parentClass === 'Level' || isRoomClass(parentClass)) &&
      Array.isArray(parentData.out_hasPart)
    ) {
      return parentData.out_hasPart.slice();
    }

    if (parentClass === 'Dashboard' && Array.isArray(parentData.out_Contains)) {
      return parentData.out_Contains.slice();
    }

    if (Array.isArray(parentData.out_hasPart)) {
      return parentData.out_hasPart.slice();
    }

    return [];
  }


  ctrl.sortBuildingsChildren = (buildings) => {
    var rids = buildings.map(n => n.attributes.rid.value);
    return persistSortOrder(ctrl, {
      loader: 'background',
      method: 'PUT',
      url: '/api/v1/location/' + ctrl.location()['@rid'].slice(1) + '/childorder',
      body: { rids: rids, parentLocationType: ctrl.location()['@class'] }
    }, 'Unable to reorder locations. The latest saved order was reloaded.');
  };

  ctrl.sortChildDatasetsAndElements = function (datasetsAndElements) {
    if (datasetsAndElements.length == 0) return;
    var parentEquipmentRid = ctrl.location()['@rid'];

    var rids = datasetsAndElements.map(n => n.attributes.rid.value);
    return persistSortOrder(ctrl, {
      loader: 'background',
      method: 'PUT',
      url: '/api/v1/customequipment/' + parentEquipmentRid.slice(1) + '/customorder',
      body: { rids: rids }
    }, 'Unable to reorder elements. The latest saved order was reloaded.');
  };

  ctrl.openBulkCreateEquipmentModal = function () {
    hideMenu(ctrl);
    bulkCreateEquipmentModal.open({
      locationRecord: ctrl.location(),
      onCreated: ctrl.init
    });
  };


  ctrl.childDashboardTemplate = function (containsEdge, wrapperClass = 'ba pa3 bw1 mb2') {
    var dashboard = containsEdge.in;
    var wrapperAttrs = wrapperClass ? { class: wrapperClass } : {};
    var useHasDashboardEdge = typeof containsEdge.useHasDashboardEdge === 'boolean'
      ? containsEdge.useHasDashboardEdge
      : ctrl.location()['@class'] !== 'Dashboard';
    return m('div', wrapperAttrs, [
      m('div.flex.justify-between.relative', [
        m(m.route.Link, { href: '/dashboard/' + dashboard['@rid'].slice(1), class: 'truncate' }, [
          dashboard.name
        ]),
        m(rowActionDropdown, {
          items: [
            {
              label: 'Edit dashboard',
              icon: 'ion-ios-compose',
              onclick: addDashboardModal.editDialogue.bind(addDashboardModal, dashboard, ctrl.location(), useHasDashboardEdge, { submitCallback: ctrl.init, delete: true })
            },
            {
              label: 'Move dashboard',
              icon: 'ion-android-exit',
              onclick: moveDashboardModal.editDialogue.bind(moveDashboardModal, containsEdge, { submitCallback: ctrl.init })
            },
            {
              label: 'Edit permissions',
              icon: 'ion-ios-people',
              onclick: function () {
                m.route.set('/dashboard/' + dashboard['@rid'].toString().slice(1) + '/permissions');
              }
            },
            {
              label: 'Copy dashboard',
              icon: 'ion-ios-copy',
              onclick: copyModal.editDialogue.bind(copyModal, dashboard, { submitCallback: ctrl.init, useHasDashboardEdge: useHasDashboardEdge })
            }
          ]
        })
      ]),

    ])
  }

  ctrl.dashboardLinkTemplate = function (dash) {
    return m('div', {
      class: 'ba pa3 bw1 mb2 mr2 pointer',
      onclick: (e) => {
        m.route.set('/dashboard/' + dash['@rid'].slice(1));
      }
    }, [
      m('a', [
        m('div.flex.justify-between', [
          m('p.pa0.ma0.truncate', dash.name),
          m('div.flex', [
            m('img', { src: '/images/dashboard_icon.svg', class: 'mr2', width: 18, height: 20}),
            m('i.icon.ion-chevron-right')
          ])
        ])
      ])
    ])
  }

  ctrl.deleteDiagram = function (diagram) {
    if (!diagram || !diagram['@rid'] || !window.confirm('Delete this diagram?')) return;
    return loaders.requestWithLoader({
      loader: 'main',
      url: '/api/v1/diagram/' + diagram['@rid'].slice(1),
      method: 'DELETE'
    }).then(function () {
      ctrl.init();
    });
  }

  ctrl.childDiagramTemplate = function (containsEdge, wrapperClass = 'ba pa3 bw1 mb2') {
    var diagram = containsEdge.in;
    var wrapperAttrs = wrapperClass ? { class: wrapperClass } : {};
    return m('div', wrapperAttrs, [
      m('div.flex.justify-between.relative', [
        m(m.route.Link, { href: '/diagram/' + diagram['@rid'].slice(1), class: 'truncate' }, [
          diagram.name
        ]),
        m(rowActionDropdown, {
          items: [
            {
              label: 'Open Diagram',
              icon: 'ion-android-open',
              onclick: function () {
                m.route.set('/diagram/' + diagram['@rid'].slice(1));
              }
            },
            {
              label: 'Delete Diagram',
              icon: 'ion-trash-a',
              onclick: ctrl.deleteDiagram.bind(ctrl, diagram)
            }
          ]
        })
      ])
    ])
  }

  ctrl.diagramLinkTemplate = function (diagram) {
    return m('div', {
      class: 'ba pa3 bw1 mb2 mr2 pointer',
      onclick: function () {
        m.route.set('/diagram/' + diagram['@rid'].slice(1));
      }
    }, [
      m('a', [
        m('div.flex.justify-between', [
          m('p.pa0.ma0.truncate', diagram.name),
          m('div.flex', [
            m('i.icon.ion-map.mr2'),
            m('i.icon.ion-chevron-right')
          ])
        ])
      ])
    ])
  }

  return ctrl.init();
};

var onupdate = async function (vnode) {
  let ctrl = this;
  if (!ctrl.location()) return;

  if (ctrl.rid !== m.route.param('id')) {
    ctrl.overrideLoadRequestId += 1;
    ctrl.rid = m.route.param('id');
    ctrl.siblingLocations([]);
    ctrl.equipmentLocations([]);
    ctrl.overrideRids([]);
    ctrl.overridesRecords([]);
    ctrl.overridesLoading(false);
    ctrl.overrideEntries([]);
    ctrl.overrideSortAscending(false);
    ctrl.overrideSortToken('desc');
    ctrl.init();
  } else if (ctrl.mapSelectedView() !== ctrl.previousMapSelectedView()) {
    ctrl.previousMapSelectedView(ctrl.mapSelectedView());
    ctrl.equipmentLocations([]);
    await ctrl.getEquipmentLocations();
    ctrl.computeFeatureGroups();
    m.redraw();
  }
}

var onremove = function (vnode) {
  this.overrideLoadRequestId += 1;
  if (this.sortErrorTimer) {
    clearTimeout(this.sortErrorTimer);
    this.sortErrorTimer = null;
  }
  modal.off('closed');
  this.pointSubscriberObj = new pointSubscriberClass();
  this.pointSubscriberObj.unsubscribeAll();
  if (typeof window !== 'undefined') {
    if (this.handleLocationDropdownViewportChange) {
      window.removeEventListener('resize', this.handleLocationDropdownViewportChange);
    }
    if (this.handleLocationDropdownScroll) {
      window.removeEventListener('scroll', this.handleLocationDropdownScroll, true);
    }
  }
};

function sortOnName(a, b) {
  var nameA = a.in.name.toLowerCase();
  var nameB = b.in.name.toLowerCase();
  if (nameA < nameB) //sort string ascending
    return -1;
  if (nameA > nameB)
    return 1;
  return 0; //default return value (no sorting)
};

function tileLocationTemplate(childEdgeRecord, ctrl) {
  return m('div.ba.b--blue.bw1.bw1.mb2.mr2.grid-location-item.thefuture', {
    rid: childEdgeRecord['@rid'],
    key: childEdgeRecord['@rid']
  }, [
    m('div.pa3.grid-location-item-drag-handle', { style: { cursor: 'grab' } }, [
      m('label.blue.f6', {
        onclick: () => { ctrl.addChildModal.editDialogue(childEdgeRecord.in, ctrl.location(), { submitCallback: ctrl.init, delete: true }) }
      }, childEdgeRecord.in.name + ' ' + (childEdgeRecord.in.roomNumber || ''))
    ])
  ])
}

function hideMenu(ctrl) {
}

function formatLocationDisplayName(locationRecord) {
  if (!locationRecord) return '';
  if (locationRecord.name === 'root') return 'Home';
  if (isRoomClass(locationRecord['@class'])) {
    var pieces = [];
    if (locationRecord.name) pieces.push(locationRecord.name);
    if (locationRecord.roomNumber) pieces.push(locationRecord.roomNumber);
    return pieces.length ? pieces.join(' ') : (locationRecord['@class'] || '');
  }
  return locationRecord.name || locationRecord['@class'] || '';
}

function humanizeClassName(name) {
  if (!name) return '';
  return String(name).replace(/([a-z])([A-Z])/g, '$1 $2').trim();
}

function getCurrentLocationEditLabel(locationRecord) {
  var locationClass = locationRecord && locationRecord['@class'];
  return `Edit ${isRoomClass(locationClass) ? 'Room' : (locationClass || 'Location')}`;
}

function getManifestEntrypoints(manifestEntry) {
  if (manifestEntry && Array.isArray(manifestEntry.entrypoints) && manifestEntry.entrypoints.length > 0) {
    return manifestEntry.entrypoints;
  }
  return ['equipment'];
}

function manifestSupportsEntrypoint(manifestEntry, entrypoint) {
  return getManifestEntrypoints(manifestEntry).indexOf(entrypoint) >= 0;
}

function collectRoomReportsForClass(category, targetClassName, multipleRecordReport, inheritedReports, availableReports) {
  if (!category || !category.name) return false;

  var currentCategoryReports = inheritedReports.slice();
  (category.reportManifest || []).forEach(function (manifestEntry) {
    if (manifestSupportsEntrypoint(manifestEntry, 'equipment')) {
      currentCategoryReports.push({ originCategory: category, rm: manifestEntry });
    }
  });

  if (category.name === targetClassName) {
    inheritedReports.forEach(function (entry) {
      var manifestEntry = entry && entry.rm;
      if (!manifestEntry) return;
      var reportName = category.name + '-' + manifestEntry.name;
      if (
        ((multipleRecordReport && manifestEntry.multipleRecord) || (!multipleRecordReport && !manifestEntry.multipleRecord)) &&
        !availableReports.some(function (reportRecord) { return reportRecord.name === reportName; })
      ) {
        availableReports.push({
          name: reportName,
          equipmentClass: category.name,
          reportType: (manifestEntry.templateName || category.name) + ':' + manifestEntry.name,
          reportMode: manifestEntry.name,
          targetClassName: category.name
        });
      }
    });

    (category.reportManifest || []).forEach(function (manifestEntry) {
      if (!manifestSupportsEntrypoint(manifestEntry, 'equipment')) return;
      var reportName = category.name + '-' + manifestEntry.name;
      if (
        ((multipleRecordReport && manifestEntry.multipleRecord) || (!multipleRecordReport && !manifestEntry.multipleRecord)) &&
        !availableReports.some(function (reportRecord) { return reportRecord.name === reportName; })
      ) {
        availableReports.push({
          name: reportName,
          equipmentClass: category.name,
          reportType: (manifestEntry.templateName || category.name) + ':' + manifestEntry.name,
          reportMode: manifestEntry.name,
          targetClassName: category.name
        });
      }
    });
    return true;
  }

  return Array.isArray(category.children) && category.children.some(function (childCategory) {
    return collectRoomReportsForClass(childCategory, targetClassName, multipleRecordReport, currentCategoryReports, availableReports);
  });
}

function getCurrentRouteQueryString() {
  if (!m.route || typeof m.route.get !== 'function') return '';
  var route = m.route.get();
  if (!route) return '';
  var separatorIndex = route.indexOf('?');
  return separatorIndex >= 0 ? route.slice(separatorIndex) : '';
}

var view = function () {
  var ctrl = this;
  if (ctrl.requestStatus() !== RequestStatus.SUCCESS) {
    return m(requestStatusHandler, {
      requestStatus: ctrl.requestStatus,
      requestStatusCode: ctrl.requestStatusCode,
    });
  }
  const datasets = (ctrl.location().out_Contains || []).filter(edge => edge?.in?.['@class'] === 'Dataset');
  const datasetHasPoints = datasets.some(edge =>
    Array.isArray(edge?.in?.out_Column) && edge.in.out_Column.some(column => column?.in?.['@rid'])
  );
  const reportMenuConfig = getLocationReportMenuConfig(ctrl, datasets, datasetHasPoints);
  var locationRecord = ctrl.location();
  var equipmentOnlyRids = filterEquipmentRids(ctrl.alarmEquipmentRids());
  var locationRid = locationRecord && locationRecord['@rid'] ? locationRecord['@rid'] : null;
  var builderFragment = locationRid ? ('location-chart-builder-' + locationRid.toString().replace(/[#:]/g, '-')) : 'location-chart-builder';
  if (locationRid) {
    if (!ctrl.chartBuilderElement || ctrl.chartBuilderElement.__locationRid !== locationRid) {
      ctrl.chartBuilderElement = {
        '@rid': '#chart-builder-' + builderFragment,
        name: 'Trend Logs',
        parentRid: locationRid,
        equipmentClasses: [],
        chartSpan: (locationRecord && locationRecord.chartSpan) || 8,
        chartResample: (locationRecord && locationRecord.chartResample) || 'hours',
        chartEnding: locationRecord && locationRecord.chartEnding ? locationRecord.chartEnding : null,
        hiddenFields: {},
        smartChartValueField: 'mean',
        __locationRid: locationRid
      };
    } else {
      ctrl.chartBuilderElement['@rid'] = '#chart-builder-' + builderFragment;
      ctrl.chartBuilderElement.name = 'Trend Logs';
      ctrl.chartBuilderElement.parentRid = locationRid;
      ctrl.chartBuilderElement.chartSpan = (locationRecord && locationRecord.chartSpan) || 8;
      ctrl.chartBuilderElement.chartResample = (locationRecord && locationRecord.chartResample) || 'hours';
      ctrl.chartBuilderElement.chartEnding = locationRecord && locationRecord.chartEnding ? locationRecord.chartEnding : null;
    }
  } else {
    ctrl.chartBuilderElement = null;
  }
  var chartBuilderElement = ctrl.chartBuilderElement;
  var renderActiveLocationLeaf = function (payload) {
    var locationRecord = payload && payload.record ? payload.record : ctrl.location();
    if (!locationRecord) return null;
    if (locationRecord.name === 'root') {
      ctrl.closeLocationDropdown();
      return m('div.mr2.dib-ns', formatLocationDisplayName(locationRecord));
    }
    var currentClass = locationRecord['@class'];

    var siblingRecords = (ctrl.siblingLocations() || []).map(function (edge) {
      if (!edge) return null;
      return edge.in || edge;
    }).filter(function (record) {
      if (!record) return false;
      if (currentClass && record['@class']) {
        var isCurrentRoom = isRoomClass(currentClass);
        var isRecordRoom = isRoomClass(record['@class']);
        if (!isCurrentRoom || !isRecordRoom) {
          if (record['@class'] !== currentClass) return false;
        }
      }
      return true;
    });
    var peerRecords = [locationRecord].concat(siblingRecords);
    peerRecords.sort(function (recordA, recordB) {
      var nameA = formatLocationDisplayName(recordA) || '';
      var nameB = formatLocationDisplayName(recordB) || '';
      var comparison = nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
      if (comparison !== 0) return comparison;
      var ridA = recordA && recordA['@rid'] ? recordA['@rid'].toString() : '';
      var ridB = recordB && recordB['@rid'] ? recordB['@rid'].toString() : '';
      return ridA.localeCompare(ridB);
    });
    var currentRid = locationRecord['@rid'] ? locationRecord['@rid'].toString() : null;
    var selectablePeerRecords = peerRecords.filter(function (record) {
      var recordRid = record && record['@rid'] ? record['@rid'].toString() : null;
      return recordRid && recordRid !== currentRid;
    });
    var hasDropdownOptions = selectablePeerRecords.length > 0;
    if (!hasDropdownOptions) ctrl.closeLocationDropdown();
    var dropdownOpen = ctrl.locationDropdownOpen();
    var dropdownVisible = ctrl.locationDropdownVisible();
    var dropdownPosition = ctrl.locationDropdownPosition() || {};
    var dropdownMaxVisibleItems = 5.5; // Show a partial row to hint scrollability
    var dropdownRowHeightRem = 2.75;
    var dropdownStyle = {
      zIndex: 1002,
      minWidth: '14rem',
      maxWidth: 'calc(100vw - 1rem)',
      position: 'fixed',
      maxHeight: (dropdownMaxVisibleItems * dropdownRowHeightRem) + 'rem',
      overflowY: 'auto'
    };
    if (dropdownPosition.left !== null && typeof dropdownPosition.left !== 'undefined') {
      dropdownStyle.left = dropdownPosition.left + 'px';
    }
    if (dropdownPosition.top !== null && typeof dropdownPosition.top !== 'undefined') {
      dropdownStyle.top = dropdownPosition.top + 'px';
    }
    if (!dropdownVisible) {
      dropdownStyle.visibility = 'hidden';
      dropdownStyle.pointerEvents = 'none';
      dropdownStyle.opacity = 0;
    } else {
      dropdownStyle.visibility = 'visible';
      dropdownStyle.pointerEvents = 'auto';
      dropdownStyle.opacity = 1;
    }
    var routeQuery = getCurrentRouteQueryString();

    var toggleDropdown = function (event) {
      if (!hasDropdownOptions) return;
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      var nextOpen = !dropdownOpen;
      if (nextOpen) {
        ctrl.locationDropdownVisible(false);
        ctrl.locationDropdownOpen(true);
        ctrl.updateLocationDropdownPosition();
      } else {
        ctrl.closeLocationDropdown();
      }
    };

    return m('div', { class: 'mr2 dib-ns relative' }, [
      m('div', {
        class: 'flex items-end bg-transparent bn white' + (hasDropdownOptions ? ' pointer' : ''),
        onclick: hasDropdownOptions ? toggleDropdown : undefined
      }, [
        m('span', formatLocationDisplayName(locationRecord)),
        hasDropdownOptions ? m('span.ml2.dib', {
          style: {
            width: 0,
            height: 0,
            borderLeft: '0.34rem solid transparent',
            borderRight: '0.34rem solid transparent',
            borderTop: '0.44rem solid #357edd',
            verticalAlign: 'middle'
          },
          oncreate: function (vnode) {
            ctrl.setLocationDropdownArrow(vnode.dom);
          },
          onupdate: function (vnode) {
            ctrl.setLocationDropdownArrow(vnode.dom);
          }
        }) : null
      ]),
      hasDropdownOptions ? m('div', {
        class: 'br2 ba pa2 bg-dark-gray ' + (dropdownOpen ? '' : ' dn'),
        style: dropdownStyle,
        onmouseleave: function () { ctrl.closeLocationDropdown(); },
        oncreate: function (vnode) { ctrl.setLocationDropdownElement(vnode.dom); },
        onupdate: function (vnode) { ctrl.setLocationDropdownElement(vnode.dom); },
        onbeforeremove: function () { ctrl.setLocationDropdownElement(null); }
      }, peerRecords.map(function (record, index) {
        if (!record) return null;
        var recordRid = record['@rid'] ? record['@rid'].toString() : null;
        var isCurrent = currentRid && recordRid === currentRid;
        var optionRowClass = 'f4 pa2 flex items-center justify-between ';
        optionRowClass += isCurrent ? 'white o-60' : 'blue dim pointer';
        var wrapperClass = index === peerRecords.length - 1 ? '' : 'bb b--mid-gray';
        var optionAttrs = {
          key: recordRid || index,
          class: wrapperClass
        };
        var rowAttrs = {
          class: optionRowClass
        };
        if (!isCurrent && recordRid) {
          rowAttrs.onclick = function () {
            ctrl.closeLocationDropdown();
            m.route.set('/location/' + recordRid.slice(1) + routeQuery);
          };
          rowAttrs.role = 'button';
          rowAttrs.tabindex = 0;
        }
        return m('div', optionAttrs, [
          m('div', rowAttrs, [
            m('span', formatLocationDisplayName(record))
          ])
        ]);
      }).filter(function (node) { return node !== null; })) : null
    ]);
  };

  var renderLocationActions = function () {
    if (edit.vm.editing() && auth.user().isSuperuser == 1) {
      switch (ctrl.location()['@class']) {
        case 'Dashboard':
          ctrl.childClass = 'Site';
          ctrl.addChildModal = addLocationModal;
          ctrl.addCurrentClassModal = addDashboardModal;
          break;
        case 'Site':
          ctrl.childClass = 'Building';
          ctrl.addChildModal = addBuildingModal;
          ctrl.addCurrentClassModal = addLocationModal;
          break;
        case 'Building':
          ctrl.childClass = 'Level';
          ctrl.addChildModal = addLevelModal;
          ctrl.addCurrentClassModal = addBuildingModal;
          break;
        case 'Level':
          ctrl.childClass = 'Room';
          ctrl.addChildModal = addRoomModal;
          ctrl.addCurrentClassModal = addLevelModal;
          break;
        case 'Room':
          ctrl.childClass = 'Room';
          ctrl.addChildModal = addRoomModal;
          ctrl.addCurrentClassModal = addRoomModal;
          break;
        default:
          if (isRoomClass(ctrl.location()['@class'])) {
            ctrl.childClass = 'Room';
            ctrl.addChildModal = addRoomModal;
            ctrl.addCurrentClassModal = addRoomModal;
          }
          break;
      }

      return m('div', { style: { 'margin-left': 'auto' } }, [
        m(rowActionDropdown, {
          items: [
            ctrl.location().name != 'root' ? {
              label: 'Edit Permissions',
              icon: 'ion-ios-people',
              onclick: function () {
                m.route.set('/location/' + ctrl.location()['@rid'].toString().slice(1) + '/permissions');
              }
            } : null,
            ctrl.location().name != 'root' ? {
              label: getCurrentLocationEditLabel(ctrl.location()),
              icon: 'ion-ios-compose',
              onclick: ctrl.addCurrentClassModal.editDialogue.bind(ctrl.addCurrentClassModal, ctrl.location(), ctrl.parentLocation(), { submitCallback: ctrl.init, delete: false })
            } : null,
            !isRoomClass(ctrl.location()['@class']) ? {
              label: `Add ${ctrl.childClass}`,
              icon: 'ion-ios-compose',
              onclick: ctrl.addChildModal.editDialogue.bind(ctrl.addChildModal, undefined, ctrl.location(), { submitCallback: ctrl.init, delete: false })
            } : null,
            {
              label: 'Add Dashboard',
              icon: 'ion-ios-compose',
              onclick: addDashboardModal.editDialogue.bind(addDashboardModal, undefined, ctrl.location(), ctrl.location()['@class'] !== 'Dashboard', { submitCallback: ctrl.init })
            },
            (isLocationClass(ctrl.location()['@class']) || isRoomClass(ctrl.location()['@class'])) ? {
              label: 'Add Diagram',
              icon: 'ion-map',
              onclick: addDiagramModal.open.bind(addDiagramModal, ctrl.location(), { submitCallback: ctrl.init })
            } : null,
            {
              label: 'Create Equipment',
              icon: 'ion-ios-compose',
              onclick: addCustomEquipmentModal.editDialogue.bind(addCustomEquipmentModal, undefined, ctrl.location(), undefined, { submitCallback: ctrl.init })
            },
            {
              label: 'Bulk Create Equipment',
              icon: 'ion-ios-upload-outline',
              onclick: function (event) {
                if (event) event.preventDefault();
                ctrl.openBulkCreateEquipmentModal();
              }
            },
            ctrl.location()['@class'] == 'Level' ? {
              label: 'Update Rooms with SVG',
              icon: 'ion-ios-compose',
              onclick: LevelSVGModal.editDialogue.bind(LevelSVGModal, ctrl.location(), { delete: false, submitCallback: ctrl.init })
            } : null,
            ctrl.location()['@class'] == 'Level' ? {
              label: 'Update Equipment with SVG',
              icon: 'ion-ios-compose',
              onclick: LevelSVGModal.editDialogue.bind(LevelSVGModal, ctrl.location(), { delete: false, equipment: true, submitCallback: ctrl.init })
            } : null
          ].filter(Boolean)
        })
      ]);
    }

    const renderWeatherToggle = function () {
      return weatherChart.renderToggle({ location: ctrl.location() });
    };

    const children = [
      m(locationDescendantSearch, { location: ctrl.location })
    ];
    const weatherToggle = renderWeatherToggle();
    if (weatherToggle) {
      children.unshift(weatherToggle);
    }

    return m('div.flex.items-center', children);
  };
  
  return m('div.pb5-ns.pb0-l', [
    m(flash, { items: ctrl.sortError(), bg: 'bg-red' }),
    m('div.mh3', [
      m(pathHeader, {
        ancestors: ctrl.location().path,
        record: ctrl.location(),
        class: '.flex.flex-wrap.items-center.justify-between',
        renderLeaf: renderActiveLocationLeaf
      }, [
        // moved reports dropdown near Connections block; removed here
        renderLocationActions()
      ]),
      // Connections dropdown (visible when there are in_containsLocation edges)
      (function () {
        var hasConnections = (ctrl.location().in_containsLocation || []).length > 0;

        if (!hasConnections) return null;

        return m('div.mb3.flex.justify-between.relative', [
          m('div'),
          m('div.flex', [
            (reportMenuConfig ? m(rowActionDropdown, {
              triggerLabel: reportMenuConfig.label,
              align: 'left',
              items: reportMenuConfig.items.map(function (item) {
                return {
                  label: item.label,
                  onclick: item.onclick
                };
              })
            }) : ''),
            m('div.flex', [
              m(hoverableDropdownDiv, {
                menuTitle: 'Connections',
                menuId: 'menu-location-connections',
                isOpen: ctrl.openDropdownId === 'menu-location-connections',
                toggleDropdown: function (id) { ctrl.openDropdownId = (ctrl.openDropdownId === id ? null : id); },
                onMouseOver: function () { ctrl.openDropdownId = 'menu-location-connections'; },
                onMouseOut: function () { ctrl.openDropdownId = null; },
                content: m('div.w-100', [
                  m('div.pv1.tc.f4.w-100', 'Fed By'),
                  m('div.w-100', [
                    (ctrl.location().in_containsLocation || [])
                      .map(function (edge) {
                        var eq = edge.out;
                        if (!eq) return null;
  
                        // Apply same backend filters for incoming/outgoing feeds
                        var incomingValid = (Array.isArray(eq.in_Feeds) ? eq.in_Feeds : [])
                          .filter(function (feedEdge) { return feedEdge?.out?.['@rid'] && !Array.isArray(feedEdge?.out?.in_hasComponent); });
                        var outgoingValid = (Array.isArray(eq.out_Feeds) ? eq.out_Feeds : [])
                          .filter(function (feedEdge) { return feedEdge?.in?.['@rid'] && !Array.isArray(feedEdge?.in?.in_hasComponent); });
  
                        // Always show the equipment row, even if no feeds exist
  
                        return m('div.grid-item.w-100.ph2.pv1', [
                          m('div.ba.b--blue.bw1.pa2', [
                            // Equipment name
                            m('div.flex.items-start', [
                              m('div.blue.mr2.pointer', {
                                onclick: function () { m.route.set('/equipment/' + eq['@class'] + '/' + eq['@rid'].slice(1)); }
                              }, (eq.name || eq['@class']))
                            ]),
  
                            // Incoming connections (filtered)
                            incomingValid && incomingValid.length
                              ? m('div.mt1', [
                                m('div.pv1.tc.f5.w-100', 'Incoming'),
                                m('div.flex.flex-column.w-100',
                                  incomingValid.map(function (feedEdge) {
                                    return m('div.flex.items-start', [
                                      feedEdge.out
                                        ? m('div.blue.mr2.pointer', {
                                          onclick: function () { m.route.set('/equipment/' + feedEdge.out['@class'] + '/' + feedEdge.out['@rid'].slice(1)); }
                                        }, (feedEdge.out.name || feedEdge.out['@class']))
                                        : null,
                                      Array.isArray(feedEdge.feedsPaths) && feedEdge.feedsPaths.length
                                        ? m('div', feedEdge.feedsPaths.map(function (feedsPath) {
                                          if (!feedsPath || !feedsPath['@rid'] || !Array.isArray(feedsPath.path) || !feedsPath.path.length) return null;
                                          return m('div.flex.flex-wrap', [
                                            m('div', '•'),
                                            feedsPath.path.map(function (node, idx) {
                                              if (!node || !node['@rid'] || (feedEdge.out && node['@rid'] == feedEdge.out['@rid'])) return null;
                                              return m('div.flex.items-center', [
                                                m(m.route.Link, {
                                                  href: '/equipment/' + node['@class'].toLowerCase() + '/' + node['@rid'].slice(1),
                                                  class: 'mh2'
                                                }, (node.name || node['@class'])),
                                                idx < feedsPath.path.length - 1 ? ' / ' : ''
                                              ]);
                                            })
                                          ]);
                                        }))
                                        : null
                                    ]);
                                  })
                                )
                              ])
                              : null,
  
                            // Outgoing connections (filtered)
                            outgoingValid && outgoingValid.length
                              ? m('div.mt2', [
                                m('div.pv1.tc.f5.w-100', 'Outgoing'),
                                m('div.flex.flex-column.w-100',
                                  outgoingValid.map(function (feedEdge) {
                                    return m('div.flex.items-start', [
                                      feedEdge.in
                                        ? m('div.blue.mr2.pointer', {
                                          onclick: function () { m.route.set('/equipment/' + feedEdge.in['@class'] + '/' + feedEdge.in['@rid'].slice(1)); }
                                        }, (feedEdge.in.name || feedEdge.in['@class']))
                                        : null,
                                      Array.isArray(feedEdge.feedsPaths) && feedEdge.feedsPaths.length
                                        ? m('div', feedEdge.feedsPaths.map(function (feedsPath) {
                                          if (!feedsPath || !feedsPath['@rid'] || !Array.isArray(feedsPath.path) || !feedsPath.path.length) return null;
                                          return m('div.flex.flex-wrap', [
                                            m('div', '•'),
                                            feedsPath.path.map(function (node, idx) {
                                              if (!node || !node['@rid'] || (feedEdge.in && node['@rid'] == feedEdge.in['@rid'])) return null;
                                              return m('div.flex.items-center', [
                                                m(m.route.Link, {
                                                  href: '/equipment/' + node['@class'].toLowerCase() + '/' + node['@rid'].slice(1),
                                                  class: 'mh2'
                                                }, (node.name || node['@class'])),
                                                idx < feedsPath.path.length - 1 ? ' / ' : ''
                                              ]);
                                            })
                                          ]);
                                        }))
                                        : null
                                    ]);
                                  })
                                )
                              ])
                              : null
                          ])
                        ])
                      })
                  ])
                ])
              })
            ])
          ]),

        ]);
      })(),
      m('div.cf.mt2', [
        m('div.fl.w-100.pb1' + (isRoomClass(ctrl.location()['@class']) ? '' : '.w-70-l.pr1'), [
          m('div.mb2.relative' + ((ctrl.location()['@class'] === 'Level' || isRoomClass(ctrl.location()['@class'])) ? '.vh-50' : '.h5'), { style: { 'z-index': '0', 'min-height': '16rem', 'position': 'relative' } }, [
            m(mapElement, {
              location: ctrl.location,
              locationConfig: ctrl.getMapConfig,
              parentLocation: ctrl.parentLocation,
              grandparentLocation: ctrl.grandparentLocation,
              childFeatures: ctrl.childLocations,
              siblingFeatures: ctrl.siblingLocations,
              grandchildFeatures: ctrl.grandchildLocations,
              systemRecord: prop(),
              mapSelectedView: ctrl.mapSelectedView,
              onFullscreenChange: (isFull) => {
                ctrl.isMapFullscreen(isFull);
                m.redraw();
              }
            }),
            m('div.map-view-selector.absolute.flex.flex-column.items-end', {
              style: { top: '1rem', right: '1rem', zIndex: 1003 }
            }, [
              m('div.relative.flex.flex-column.items-end', [
                m('span.ba.b--blue.br2.ph1.pv1.f3.flex.items-center.pb2.pointer', {
                  onclick: () => {
                    if (document.getElementById('menu-classes-dropdown')) {
                      if (document.getElementById('menu-classes-dropdown').classList.contains('dn'))
                        document.getElementById('menu-classes-dropdown').classList.remove('dn');
                      else
                        document.getElementById('menu-classes-dropdown').classList.add('dn');
                    }
                  }
                }, [
                  m('span.mr2', ctrl.mapSelectedView()),
                  (() => {
                    if (document.getElementById('menu-classes-dropdown') && !document.getElementById('menu-classes-dropdown').classList.contains('dn')) {
                      return m('i.mr1', {
                        class: 'blue ion-ios-arrow-up',
                      })
                    } else {
                      return m('i.mr1', {
                        class: 'blue ion-ios-arrow-down',
                      })
                    }
                  })(),
                ]),
                m('div.br2.ba.pa2.bg-dark-gray.absolute.dn', {
                  style: { zIndex: 1002, top: 'calc(100%)', right: 0 },
                  id: 'menu-classes-dropdown',
                  onmouseover: () => document.getElementById('menu-classes-dropdown').classList.remove('dn'),
                  onmouseout: () => document.getElementById('menu-classes-dropdown').classList.add('dn')
                }, (ctrl.mapModes() || []).map((category, idx) => {
                  return ctrl.mapSelectedView() != category.name ?
                    m('a.blue.dim.f4.pa2.h-100.flex.items-center.bb',
                      {
                        onclick: async () => {
                          try {
                            ctrl.mapSelectedView(category.name);
                            document.getElementById('menu-classes-dropdown').classList.add('dn');
                          } catch (error) {
                            console.error('Error in onclick handler:', error);
                          }
                        }
                      }, [
                      m('span', `${category.name}`)
                    ])
                    : ''
                }))
              ])
            ])
          ]),
        ]),
        // Location tiles and dashboards sidebar
        isRoomClass(ctrl.location()['@class']) ? null :
          m('div.fl.w-100.pb1.w-30-l.f3' + ((ctrl.location()['@class'] === 'Level' || isRoomClass(ctrl.location()['@class'])) ? '.vh-50' : '') + (ctrl.childLocations().length > 0 || (ctrl.dashboards && ctrl.dashboards.length > 0) || (ctrl.diagrams && ctrl.diagrams.length > 0) ? '' : '.dn'), {
            style: ctrl.isMapFullscreen() ? { display: 'none' } : {}
          }, [
            m('div.div-scrollable' + ((ctrl.location()['@class'] === 'Level' || isRoomClass(ctrl.location()['@class'])) ? '.vh-50' : '.h5'), {
              style: {
                'overflow-x': 'hidden',
                'overflow-y': 'scroll',
              }
            }, [
              m('div.fl.w-100', [
                ctrl.dashboards && ctrl.dashboards.length > 0 && edit.vm.editing() ?
                  ctrl.dashboards.filter(function (record) {
                    return record.in['@class'] === 'Dashboard';
                  })
                    .sort(sortOnName)
                    .map(function (child) {
                      return ctrl.childDashboardTemplate(child, 'ba pa3 bw1 mb2 mr2');
                    })
                  : '',
                ctrl.dashboards && ctrl.dashboards.length > 0 && !edit.vm.editing() ?
                  ctrl.dashboards.filter(function (record) {
                    return record.in['@class'] === 'Dashboard';
                  })
                    .sort(sortOnName)
                    .map(function (child) {
                      return ctrl.dashboardLinkTemplate(child.in);
                    })
                  : '',
                ctrl.diagrams && ctrl.diagrams.length > 0 && edit.vm.editing() ?
                  ctrl.diagrams
                    .sort(sortOnName)
                    .map(function (child) {
                      return ctrl.childDiagramTemplate(child, 'ba pa3 bw1 mb2 mr2');
                    })
                  : '',
                ctrl.diagrams && ctrl.diagrams.length > 0 && !edit.vm.editing() ?
                  ctrl.diagrams
                    .sort(sortOnName)
                    .map(function (child) {
                      return ctrl.diagramLinkTemplate(child.in);
                    })
                  : '',
                edit.vm.editing() ?
                  m(Draggable, { onsort: ctrl.sortBuildingsChildren, draggable: '.grid-location-item', handleClass: '.grid-location-item-drag-handle' }, [
                    ctrl.childLocations().filter(c => isLocationClass(c.in['@class'])).sort(sortOnIndex).map(childEdge => {
                      return tileLocationTemplate(childEdge, ctrl)
                    })
                  ])
                  :
                  ctrl.childLocations().filter(c => isLocationClass(c.in['@class'])).sort(sortOnIndex).map(childLocation => {
                    let params = m.parseQueryString(m.route.get().split('?')[1]);
                    let baseRoute = `/location/${childLocation.in['@rid'].slice(1)}`;
                    let queryParams = '';

                    if (Object.keys(params).length > 0) {
                      let currentQuery = m.route.get().split('?')[1];
                      queryParams = params.group === 'building'
                        ? `?${currentQuery.replace(/building/g, "location")}`
                        : `?${currentQuery}`;
                    }

                    let route = baseRoute + queryParams;
                    let imgUrl;
                    if (childLocation.in['@class'] === 'Site') {
                      imgUrl = '/images/site_icon.svg';
                    } else if (childLocation.in['@class'] === 'Building') {
                      imgUrl = '/images/business-sharp.svg';
                    } else if (childLocation.in['@class'] === 'Level') {
                      imgUrl = '/images/level_icon.svg';
                    } else if (isRoomClass(childLocation.in['@class'])) {
                      imgUrl = '/images/room_icon.svg';
                    }
                    return m(m.route.Link, { href: route, class: 'no-underline ba pa3 bw1 mb2 mr2 db' }, [
                      m('div.flex.justify-between.items-center', [
                        m('span.truncate', {
                          style: { flex: '1 1 0%', minWidth: 0 },
                          title: `${childLocation.in.name} ${childLocation.in.roomNumber || ''}`
                        }, `${childLocation.in.name} ${childLocation.in.roomNumber || ''}`),
                        m('div.flex.items-center', {
                          style: { flexShrink: 0 }
                        }, [
                          m('img', { src: imgUrl, class: 'mr2', width: 18, height: 20, alt: 'Grid view' }),
                          m('i', { class: 'icon ion-chevron-right' }),
                        ])
                      ]),
                    ]);
                  }),
              ])
            ])
          ]),
      ]),
    ]),
    m('div', { style: ctrl.isMapFullscreen() ? { display: 'none' } : {} }, [
      isRoomClass(ctrl.location()['@class']) ? // Todo Validate con Luke Design
        m(`div.cf${edit.vm.editing() ? '.mh3' : ''}`, [
          m('div', [
            (ctrl.location().out_hasDashboard || [])
              .sort(sortOnName)
              .map(function (outHasDahsboardEdge) {
                let dash = outHasDahsboardEdge.in;
                return edit.vm.editing() ?
                  ctrl.childDashboardTemplate(outHasDahsboardEdge)
                  :
                  m('div.fl.w-100.w-50-ns.w-25-l.ph3', [
                    m(m.route.Link, { href: '/dashboard/' + dash['@rid'].slice(1) }, [
                      m('div.flex.justify-between.items-center.ba.b--mid-gray.bw1.pa2.mb3.f6', [
                        m('p.pa0.ma0.truncate', dash.name),
                        m('i.icon.ion-ios-arrow-right')
                      ]),
                    ])
                  ])
              })
          ]),
          m('div', [
            (ctrl.location().out_hasDiagram || [])
              .sort(sortOnName)
              .map(function (outHasDiagramEdge) {
                let diagram = outHasDiagramEdge.in;
                return edit.vm.editing() ?
                  ctrl.childDiagramTemplate(outHasDiagramEdge)
                  :
                  m('div.fl.w-100.w-50-ns.w-25-l.ph3', [
                    m(m.route.Link, { href: '/diagram/' + diagram['@rid'].slice(1) }, [
                      m('div.flex.justify-between.items-center.ba.b--mid-gray.bw1.pa2.mb3.f6', [
                        m('p.pa0.ma0.truncate', diagram.name),
                        m('i.icon.ion-ios-arrow-right')
                      ]),
                    ])
                  ])
              })
          ]),
        ])
        : '',
      m('div.cf', [
        edit.vm.editing() ?
          m(heading, { label: 'Elements', class: 'f2 mb2 mh3' })
          : '',
        datasetsAndElementsTemplate(ctrl)
      ]),
      m('div.cf.mh3', [
        edit.vm.editing() ?
          [
            m('div.w-100.w-50-ns.w-50-l.pv1.pr2.fl', [
              m('div.ba.b--dashed.bw1.pa2', {
                onclick: function () {
                  var location = ctrl.location && ctrl.location();
                  if (!location || !location['@rid']) {
                    return;
                  }
                  addDatasetOrElementModal.open({
                    location: ctrl.location,
                    submitCallback: ctrl.init
                  });
                }
              }, [
                m('div.flex.justify-center.blue', [
                  m('i.ion-plus.mr2'),
                  m('div', 'New Element')
                ])
              ])
            ]),
            m('div.w-100.w-50-ns.w-50-l.pv1.fr', [
              m('div.ba.b--dashed.bw1.pa2', {
                  onclick: function () {
                    var location = ctrl.location && ctrl.location();
                    if (!location || !location['@rid']) {
                      return;
                    }
                    addCustomEquipmentModal.editDialogue(undefined, location, undefined, {
                      submitCallback: ctrl.init
                    });
                  }
                }, [
                  m('div.flex.justify-center.blue', [
                    m('i.ion-plus.mr2'),
                    m('div', 'Create Equipment')
                  ])
                ])
              ])
            
          ]
          :
          ''
      ]),
      m('div.cf.mh3', [
        m(alarmsTable, {
          equipmentAllRids: equipmentOnlyRids,
          dashboardsAllRids: getDashboardRidsForLocation(ctrl.location()),
          location: ctrl.location,
          equipmentTableLoading: ctrl.alarmEquipmentLoading,
        }),
        m('div.w-100', [
          m(eventTable, { location: ctrl.location() })
        ])
      ]),
      edit.vm.editing() ? null : m(overridesComponent, {
        rids: ctrl.overrideRids,
        records: ctrl.overridesRecords,
        isLoading: ctrl.overridesLoading,
        location: ctrl.location,
        sortAscending: ctrl.overrideSortAscending,
        sortToken: ctrl.overrideSortToken(),
        refreshToken: ctrl.overrideRefreshToken(),
        onToggleSort: ctrl.toggleOverrideSort,
        onOverridesChanged: ctrl.loadOverrides,
        onReleaseComplete: ctrl.loadOverrides
      }),
      edit.vm.editing() ? null : m(eventsComponent, {
      }),
      edit.vm.editing() ?
        null
        :
        m(searchEquipmentComponent, {
          searchEquipmentActive: ctrl.searchEquipmentActive,
          searchEquipmentValue: ctrl.searchEquipmentValue,
          searchedEquipmentRids: ctrl.searchedEquipmentRids,
          preferredNumberOfRowsValue: ctrl.preferredNumberOfRowsValue,
          equipmentToShow: ctrl.equipment,
          equipmentClassesSelected: ctrl.equipmentClassesSelected,
          equipmentTableLoading: ctrl.equipmentTableLoading,
          preferredNumberOfRows: ctrl.preferredNumberOfRows,
          equipmentClassesArray: ctrl.equipmentClassesArray,
          location: ctrl.location,
          equipment: ctrl.equipment,
        }),
      (edit.vm.editing() || !locationRid || !chartBuilderElement) ?
        null
        :
        m('div.cf.mh3', [
          m(smartChart, {
            element: function () { return chartBuilderElement; },
            variant: 'chartBuilder',
            builderFragment: builderFragment,
            parentReinitialize: ctrl.init
          })
        ]),
    ]),
  ]);
};


function sortOnIndex(a, b) {
  return a.index - b.index;
}

function filterEquipmentRids(records) {
  var equipmentMap = equipmentModel.equipmentClassTree && equipmentModel.equipmentClassTree.equipment;
  if (!equipmentMap || !Object.keys(equipmentMap).length) return records || [];
  return (records || []).filter(record => equipmentMap[record['@class']]);
}

function ensureClassOption(options, value) {
  if (!options || !value) return;
  if (options.find(entry => entry.value === value)) return;
  options.unshift({ value: value, key: value });
}


function datasetsAndElementsTemplate(ctrl) {
  var datasets = (ctrl.location().out_Contains || [])
    .filter(outContainsedge => outContainsedge && outContainsedge.in && outContainsedge.in['@class'] === 'Dataset');
  var elements = (ctrl.location().out_hasElement || [])
  var datasetsAndElementsComponents = datasets.concat(elements)
    .filter(edge => edge && edge.in)
    .sort((a, b) => (a.index || 0) - (b.index || 0))
    .map(function (edge) {
      if (!edge || !edge.in) {
        return null;
      }
      if (edge.in.type === 'smartTable') {
      }

      if (edge.in['@class'] === 'Element') {
        return m(element, {
          child: edge,
          key: edge.in['@rid'],
          dashboardCallback: ctrl.init,
          parentRid: ctrl.location()['@rid'],
          location: ctrl.location
        })
      } else {
        let datasetRecord = edge.in;
        let fakeElement = {
          key: datasetRecord['@rid'],
          in: {
            '@rid': datasetRecord['@rid'],
            name: datasetRecord.name,
            type: datasetRecord.type,
            out_Contains: [{ ...edge }]
          },
          permission: datasetRecord.permission
        }
        fakeElement['@rid'] = edge['@rid'];
        return m(element, {
          key: datasetRecord['@rid'],
          child: fakeElement,
          dashboardCallback: ctrl.init,
          datasetRepresentation: true,
          parentRid: ctrl.location()['@rid'],
          location: ctrl.location
        })
      }
    })
    .filter(Boolean);
  if (edit.vm.editing()) {
    datasetsAndElementsComponents.unshift(m('div.grid-sizer', { key: 0 }));
    return m(Draggable, { onsort: ctrl.sortChildDatasetsAndElements, draggable: '.grid-item', handleClass: '.grid-item-drag-handle' }, datasetsAndElementsComponents)
  } else {
    datasetsAndElementsComponents.unshift(m('div.grid-item.w-100.w-50-ns.w-25-l', { key: 0 })); //Fake div for get Masonry work
    datasetsAndElementsComponents.unshift(m('div.grid-sizer', { key: 1 }));
    return m('div.cf', { onupdate: masonry }, datasetsAndElementsComponents)
  }
}

function getLocationReportMenuConfig(ctrl, datasets, datasetHasPoints) {
  const location = ctrl.location();
  if (!location) return null;

  const locationClass = location['@class'];
  if (!isRoomClass(locationClass)) return null;
  if (!datasets.length || !datasetHasPoints) return null;
  const locationRid = location['@rid'] ? location['@rid'].slice(1) : null;
  if (!locationRid) return null;
  if (!roomClassTreeCache || !Array.isArray(roomClassTreeCache.categories)) return null;

  var availableReports = [];
  roomClassTreeCache.categories.some(function (category) {
    return collectRoomReportsForClass(category, locationClass, false, [], availableReports);
  });

  if (!availableReports.length) return null;

  return {
    label: 'Reports',
    items: availableReports.map(function (reportRecord) {
      var baseType = reportRecord.targetClassName;
      var mode = reportRecord.reportMode;
      var classLabel = humanizeClassName(locationClass || baseType);
      return {
        key: reportRecord.name,
        label: mode ? (mode.charAt(0).toUpperCase() + mode.slice(1)) : reportRecord.name,
        onclick: async function () {
          var savedReportRecord = await ctrl.findSavedLocationReportRecord(reportRecord.reportType, locationRid);
          if (savedReportRecord && savedReportRecord['@rid']) {
            downloadReportModalInterface.editDialogue({
              modalTitle: 'Download ' + (savedReportRecord.name || (classLabel + ' Report')),
              reportType: savedReportRecord.type || reportRecord.reportType,
              reportRid: savedReportRecord['@rid'],
              reportRecord: savedReportRecord,
              sourceKind: 'reportRecord'
            });
            return;
          }

          if (mode === 'detail') {
            downloadReportModalInterface.editDialogue({
              modalTitle: 'Download ' + classLabel + ' Detail Report',
              reportType: reportRecord.reportType,
              reportMode: mode,
              targetKind: 'equipment',
              targetRid: locationRid,
              targetClassName: baseType
            });
            return;
          }

          var options = {
            modalTitle: 'Download ' + location.name + ' ' + classLabel + ' ' + mode.charAt(0).toUpperCase() + mode.slice(1) + ' Report',
            reportType: reportRecord.reportType,
            reportMode: mode,
            targetKind: 'location',
            targetRid: locationRid,
            targetClassName: baseType
          };
          if (mode !== 'live') {
            options.dates = ctrl.reportStartDateAndEndDate();
          }
          downloadReportModalInterface.editDialogue(options);
        }
      };
    })
  };
}
module.exports = {
  oninit,
  onremove,
  onupdate,
  view,
  _private: {
    persistSortOrder
  }
};
